/**
 * @file @/common/constants.ts
 * @description 公共常量
 * @author heyongjie@baidu.com
 */
import {isArray, map, isPlainObject, compact} from 'lodash';
import {getLanguage} from './i18n';


// eslint-disable-next-line no-underscore-dangle
export const isSubApp = !!window.__POWERED_BY_QIANKUN__;

// RUN_PLATFORM 是 webpack 打包时利用 webpack plugin 注入尽量的变量
export const isEop = RUN_PLATFORM === 'EOP';

export const EMPTY_TEXT = '-';

export const VALIDATION_CODE = 'BceValidationException';


const CHARGE_TYPE_MAP: Record<string, string | Record<string, string>> = {
    'cpt1': '按时长',
    'cpt2': '包年包月',
    'cpt3': '按量按时长',
    'cpc': '按消耗量',
    'cpt1_cdn_bandwidth': 'CDN按带宽计费',
    'peakbandwidth_avg': '月均值',
    'eip_postpay_nettraffic': '按需按使用流量计费',
    'eip_prepay_bandwidth': '包年包月按带宽计费',
    'eip_postpay_bandwidth': '按需按带宽计费',
    'peakbandwidth_percent_95': '按月95峰值带宽计费',
    'lss_netraffic': '按使用流量计费',
    'lss_bandwidth': '按日峰值带宽计费',
    'lss_peakbandwidth_top_1': '大客户月峰值计费',
    'lss_peakbandwidth_avg': '月均值',
    'lss_peakbandwidth_percent_95': '95%峰值',
    'lss_peakbandwidth_top_4': '第4峰值',
    'vod_netraffic': '按使用流量计费',
    'vod_bandwidth': '按日峰值带宽计费',
    'vod_peakbandwidth_percent_95': '按月峰值带宽计费',
    'onetime': '一次性',
    'ai_daily': '按日',
    // BML和众测的计费方式属于预付费中的按项目，因此增加新的project类型
    // 不同服务按项目计费的文案不同
    'project': {
        BML: '按个数',
        MAT: '按次计费',
        QSS: '按次计费',
        AAT: '按次计费',
        FACE: '按次计费',
    },
    'package': '量包',
    // EIP计费变更，仅前端使用
    'netraffic': '按流量',
    'bandwidth': '按带宽',
    // 竞价实例相关
    'bidding': '按时长-抢占',
};

export const getChargeTypeText = (chargeTypeArr: string[]|undefined, serviceType: string, subProductType?: string) => {
    if (!chargeTypeArr?.length || !isArray(chargeTypeArr)) {
        return EMPTY_TEXT;
    }
    const chargeTypeNames = map(chargeTypeArr, chargeType => {
        if (getLanguage() === 'en-us') {
            return chargeType;
        }
        const chargeTypeLower = chargeType?.toLowerCase() || '';
        // 为RDS特殊处理
        if (serviceType === 'RDS' && chargeTypeLower === 'cpc') {
            return '流量另计';
        }
        // 为CDN大客户做特殊处理
        if (serviceType === 'CDN' && chargeTypeLower.startsWith('peakbandwidth_percent_')) {
            return `${chargeTypeLower.substring(22)}%峰值计费`;
        }
        if (serviceType === 'CDN' && chargeTypeLower.startsWith('peakbandwidth_top_')) {
            const n = chargeTypeLower.substring(18);
            return n === '1' ? '月峰值' : `按月第${n}峰值`;
        }
        if (serviceType === 'CSN' && subProductType === 'ByTraffic') {
            return '按流量计费';
        }
        if (CHARGE_TYPE_MAP[chargeTypeLower]) {
            // 按项目计费前端显示文案不同，增加了服务维度的配置
            if (isPlainObject(CHARGE_TYPE_MAP[chargeTypeLower])) {
                return (CHARGE_TYPE_MAP[chargeTypeLower] as Record<string, string>)[serviceType] || '';
            }
            return CHARGE_TYPE_MAP[chargeTypeLower] as string;
        }
        return chargeType;
    });
    return (
        <div>
            {compact(chargeTypeNames).map(name => (<div key={name}>{name}</div>))}
        </div>
    );
};

export const SERVICE_UNIT_MAP: Record<string, string> = {
    BCC: '台',
    CDS: '个',
    RDS: '台',
    DRDS: '台',
    BCH: '台',
    BAEPRO: '月',
    EIP: '个',
    DCC: '台',
    SCS: '台',
    BMR: '个',
    IOT: '阶梯定价',
    ADAS: '个',
    TSDB: '阶梯定价',
    VPN: '个',
    EIPGROUP: '个',
    BBC: '台',
    NAT: '个',
    WAF: '个',
    PEERCONN: '个',
    CSN: '个',
    BCHIP: '月',
    BCH_WAF: '个',
    DNS: '个',
    MONGODB: '台',
};

export const TIMEUNIT_MAP: Record<string, string> = {
    DAY: '天',
    MONTH: '个月',
    YEAR: '年',
};

export const isConsoleOnline = location.host === 'console.bce.baidu.com';

export const isEopOnline = location.host === 'eop.baidu-int.com';

export const isLocal = location.origin.includes('local');

export const isSandbox = location.origin.includes('sandbox');

export const isDev = isLocal || isSandbox;

export const UNLIMITED_QUOTA = 999999999999;

export const RED_REASONS = [
    {label: '发票信息错误', value: '发票信息错误'},
    {label: '发票类型错误(增值税普票/增值税专票)', value: '发票类型错误(增值税普票/增值税专票)'},
    {label: '工商主体变更', value: '工商主体变更'},
    {label: '发生退款需要退票', value: '发生退款需要退票'},
    {label: '发票金额有误', value: '发票金额有误'},
    {label: '其他', value: '其他'},
];
